/**
 * "How well off is this company" - the sales-facing money view on the register
 * detail page. Three sources, in order of freshness:
 *   1. EMTA quarterly data (turnover, employees, taxes, and the year trend)
 *   2. Annual report indicators (profit, equity, assets - the depth)
 *   3. Turnover split by activity (what the company earns from)
 *
 * All amounts are cents, formatted through `formatEurShort/Full`. Charts render
 * through the shadcn/ui chart layer (Recharts) with brand colours from CSS
 * variables. What each chart is allowed to claim lives in `lib.ts`.
 */
import { TrendingUp, Wallet, Users, Coins, PieChart, Landmark } from "lucide-react";
import { cn } from "@wa/ui";
import { formatEurShort, type ArDetail } from "../../../../lib/ariregister";
import { ActivityBars } from "./ActivityBars";
import { MoneyBars } from "./MoneyBars";
import { ReportTable } from "./ReportTable";
import { emtaHidesRevenue, latestQuarterSeries, marginPct, revenueTrend, type T } from "./lib";

function Tile({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: typeof Wallet;
  label: string;
  value: string;
  sub?: string;
  tone?: "pos" | "neg" | "default";
}) {
  return (
    <div className="rounded-lg border border-border bg-background/60 p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="size-3.5" /> {label}
      </div>
      <div
        className={cn(
          "mt-1 text-lg font-bold tabular-nums",
          tone === "pos" && "text-emerald-600 dark:text-emerald-400",
          tone === "neg" && "text-destructive",
        )}
      >
        {value}
      </div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function FinancialProfile({ e, t }: { e: ArDetail; t: T }) {
  const reports = [...e.reports].sort((a, b) => b.year - a.year);
  const latestReport = reports[0];
  const tax = e.taxData ?? [];
  if (tax.length === 0 && reports.length === 0) return null;

  const trend = revenueTrend(tax, reports);
  const quarters = latestQuarterSeries(tax);
  // Say it out loud rather than leave a hole where a chart should be: EMTA
  // publishes no turnover for roughly one company in eight (non-profits,
  // businesses under the VAT threshold), and a silent gap reads as a bug.
  const noEmtaRevenue = emtaHidesRevenue(tax) && trend?.source !== "emta";

  // Turnover by activity, newest reported year - the whole-company view. The
  // per-year split sits inside the table rows.
  const actYear = e.reportActivities.length ? Math.max(...e.reportActivities.map((a) => a.year)) : null;
  const acts = e.reportActivities
    .filter((a) => a.year === actYear && a.revenueCents != null && a.revenueCents > 0)
    .sort((a, b) => (b.revenueCents ?? 0) - (a.revenueCents ?? 0));

  const latestMargin = latestReport ? marginPct(latestReport.profitCents, latestReport.revenueCents) : null;
  const chartCount = (trend ? 1 : 0) + (quarters ? 1 : 0);

  return (
    <section className="rounded-xl border border-border bg-card/80 p-5 shadow-soft backdrop-blur">
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold">
        <Coins className="size-4 text-primary" /> {t("ariregister.financial.title")}
      </h2>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {e.taxRevenueCents != null && (
          <Tile
            icon={Wallet}
            label={t("ariregister.financial.turnover")}
            value={formatEurShort(e.taxRevenueCents)}
            sub={t("ariregister.financial.emtaYear", { year: e.taxYear ?? "" })}
          />
        )}
        {e.taxEmployees != null && (
          <Tile
            icon={Users}
            label={t("ariregister.financial.employees")}
            value={String(e.taxEmployees)}
            sub={t("ariregister.financial.emtaYear", { year: e.taxYear ?? "" })}
          />
        )}
        {e.reportProfitCents != null && (
          <Tile
            icon={TrendingUp}
            label={t("ariregister.financial.netProfit")}
            value={formatEurShort(e.reportProfitCents)}
            tone={e.reportProfitCents >= 0 ? "pos" : "neg"}
            sub={t("ariregister.financial.reportYear", { year: e.reportYear ?? "" })}
          />
        )}
        {e.reportEquityCents != null && (
          <Tile
            icon={Landmark}
            label={t("ariregister.financial.equity")}
            value={formatEurShort(e.reportEquityCents)}
            sub={t("ariregister.financial.reportYear", { year: e.reportYear ?? "" })}
          />
        )}
        {e.taxStateTaxCents != null && (
          <Tile
            icon={Coins}
            label={t("ariregister.financial.stateTax")}
            value={formatEurShort(e.taxStateTaxCents)}
            sub={t("ariregister.financial.emtaYear", { year: e.taxYear ?? "" })}
          />
        )}
        {e.reportAssetsCents != null && (
          <Tile
            icon={Landmark}
            label={t("ariregister.financial.assets")}
            value={formatEurShort(e.reportAssetsCents)}
            sub={t("ariregister.financial.reportYear", { year: e.reportYear ?? "" })}
          />
        )}
        {latestReport?.revenueCents != null && (
          <Tile
            icon={Wallet}
            label={t("ariregister.financial.reportRevenue")}
            value={formatEurShort(latestReport.revenueCents)}
            sub={t("ariregister.financial.reportYear", { year: latestReport.year })}
          />
        )}
        {latestMargin != null && (
          <Tile
            icon={TrendingUp}
            label={t("ariregister.financial.margin")}
            value={`${latestMargin.toFixed(1)}%`}
          />
        )}
      </div>

      {/* Trends. One chart takes the full width rather than leaving a hole. */}
      {chartCount > 0 && (
        <div className={cn("mt-5 grid gap-5", chartCount > 1 && "sm:grid-cols-2")}>
          {trend && (
            <MoneyBars
              title={t(
                trend.source === "emta"
                  ? "ariregister.financial.revenueTrend"
                  : "ariregister.financial.revenueTrendReport",
              )}
              icon={TrendingUp}
              points={trend.points}
              seriesLabel={t("ariregister.financial.turnover")}
            />
          )}
          {quarters && (
            <MoneyBars
              title={t(
                quarters.metric === "revenue"
                  ? "ariregister.financial.quarters"
                  : "ariregister.financial.quartersTax",
                { year: quarters.year },
              )}
              points={quarters.points}
              seriesLabel={t(
                quarters.metric === "revenue"
                  ? "ariregister.financial.turnover"
                  : "ariregister.financial.stateTax",
              )}
              color="var(--color-primary-2)"
              maxBarSize={56}
            />
          )}
        </div>
      )}

      {noEmtaRevenue && (
        <p className="mt-3 text-[11px] text-muted-foreground">{t("ariregister.financial.noEmtaRevenue")}</p>
      )}

      {reports.length > 0 && (
        <ReportTable reports={reports} tax={tax} activities={e.reportActivities} t={t} />
      )}

      {acts.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <PieChart className="size-3.5" />
            {t("ariregister.financial.revenueByActivity", { year: actYear ?? "" })}
          </div>
          <ActivityBars rows={acts} t={t} />
        </div>
      )}
    </section>
  );
}
